import Page from 'components/Page';
import LoadingScreen from 'components/LoadingScreen';
import useUserAccountState from 'hooks/useUserAccountState';
import { useListIngestionTokens } from 'api/frontend';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import { RouterLink } from 'components/RouterLink';
import useAccountIdRoute from 'hooks/useAccountIdRoute';
import {
  Breadcrumbs,
  CodeBlock,
  LearnMoreAccordion,
  StepBase,
} from 'subframe/index';
import { TabsWithContent } from 'components/design-system/Tabs';
import { CopyToClipboard } from 'components/design-system';

export default function AddCliCluster() {
  const { account } = useUserAccountState();
  const { data: tokens } = useListIngestionTokens('default', {
    request: { headers: { Authorization: 'Bearer ' + account?.token } },
    swr: {
      revalidateOnFocus: false,
    },
  });
  const { logEvent } = AnalyticsEventLogger();

  const clustersPath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/clusters',
  );
  const logEventWithDetails = (type: string) => {
    logEvent('breadcrumbs-navigation', { type });
  };

  if (tokens === undefined) {
    return <LoadingScreen />;
  }

  return (
    <Page
      title="Run via CLI"
      id="add-cli-cluster-page"
      data-cy="add-cli-cluster-page"
      className="ml-8"
    >
      <div
        className="container max-w-none flex h-full flex-col items-start gap-4 bg-default-background pt-8 pr-3 pl-3"
        id="add-cli-cluster-container"
        data-cy="add-cli-cluster-container"
      >
        <Breadcrumbs>
          <Breadcrumbs.Item>Risk Ledger</Breadcrumbs.Item>
          <Breadcrumbs.Divider />
          <RouterLink
            to={clustersPath}
            onClick={() => {
              logEventWithDetails('clusters-view-page');
            }}
          >
            <Breadcrumbs.Item active={true}>Clusters</Breadcrumbs.Item>{' '}
          </RouterLink>
          <Breadcrumbs.Divider />
          <Breadcrumbs.Item
            active={true}
            id="cluster-name"
            onClick={() => {
              logEventWithDetails('add-cluster-page');
            }}
          >
            Run via CLI
          </Breadcrumbs.Item>
        </Breadcrumbs>
        <div className="flex w-full flex-col items-start gap-2">
          <span className="w-full whitespace-pre-wrap text-body font-body text-default-font">
            The{' '}
            <code className="bg-neutral-100 border border-solid border-neutral-border font-monospace-body rounded py-0 px-[2px]">
              chkk
            </code>{' '}
            command-line interface (CLI) offers a developer-first way to explore
            the Chkk universe.
          </span>
          <LearnMoreAccordion title="Learn more about Dry Run mode">
            <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-100 px-2 py-2">
              <div className="flex w-full items-center gap-2">
                <StepBase.TextWithLink
                  className="h-auto grow shrink-0 basis-0 ml-2"
                  label="Learn more about Chkk CLI at:"
                  url="https://docs.chkk.io/product/chkkcli/overview.mdx"
                  urlTitle="Chkk CLI Documentation"
                />
              </div>
            </div>
          </LearnMoreAccordion>
          <StepBase
            stepTitle="Install Chkk CLI"
            stepBody=""
            stepNumber="1"
            actionButtons={<></>}
            stepTitleTooltip={<></>}
            badges={<></>}
            defaultOpen={true}
          >
            <TabsWithContent
              tabs={[
                {
                  id: 'linux',
                  title: 'Linux',
                  content: (
                    <>
                      <span className="w-full text-body font-body text-default-font">
                        Install a pre-built binary on Linux using the following
                        instructions.
                      </span>
                      <CopyToClipboard
                        className="h-auto w-full flex-none"
                        text={`# For AMD64 / x86_64
[ $(uname -m) = x86_64 ] && curl -Lo ./chkk https://cli.chkk.io/download/latest/chkk-linux-amd64
# For ARM64
[ $(uname -m) = aarch64 ] && curl -Lo ./chkk https://cli.chkk.io/download/latest/chkk-linux-arm64
chmod +x ./chkk
sudo mv ./chkk /usr/local/bin/chkk`}
                      />
                      <span className="w-full text-body font-body text-default-font">
                        Or using Brew.
                      </span>
                      <CopyToClipboard
                        className="h-auto w-full flex-none"
                        text={`brew install chkk-io/cli/chkk`}
                      />
                    </>
                  ),
                },
                {
                  id: 'mac',
                  title: 'MacOS',
                  content: (
                    <>
                      <span className="w-full text-body font-body text-default-font">
                        Install using Homebrew on MacOS.
                      </span>
                      <CopyToClipboard
                        className="h-auto w-full flex-none"
                        text={'brew install chkk-io/cli/chkk'}
                      />
                      <span className="w-full text-body font-body text-default-font">
                        Or install a pre-built binary using the following
                        instructions.
                      </span>
                      <CopyToClipboard
                        className="h-auto w-full flex-none"
                        text={`# For Intel Macs
[ $(uname -m) = x86_64 ] && curl -Lo ./chkk https://cli.chkk.io/download/latest/chkk-darwin-amd64
# For M1 / ARM Macs
[ $(uname -m) = arm64 ] && curl -Lo ./chkk https://cli.chkk.io/download/latest/chkk-darwin-arm64
chmod +x ./chkk
mv ./chkk /some-dir-in-your-PATH/chkk
`}
                      />
                    </>
                  ),
                },
                {
                  id: 'windows',
                  title: 'Windows',
                  content: (
                    <>
                      <span className="w-full text-body font-body text-default-font">
                        Install a pre-built binary on Windows in PowerShell.
                      </span>
                      <CopyToClipboard
                        className="h-auto w-full flex-none"
                        text={`curl.exe -Lo chkk-windows-amd64.exe https://cli.chkk.io/dwonload/latest/chkk-windows-amd64
Move-Item .\\chkk-windows-amd64.exe c:\\some-dir-in-your-PATH\\chkk.exe
`}
                      />
                    </>
                  ),
                },
              ]}
            />
          </StepBase>
          <StepBase
            stepTitle="Get Started"
            stepBody=""
            stepNumber="2"
            actionButtons={<></>}
            stepTitleTooltip={<></>}
            badges={<></>}
            defaultOpen={true}
          >
            <span className="w-full whitespace-pre-wrap text-body font-body text-default-font">
              The{' '}
              <code className="bg-neutral-100 border border-solid border-neutral-border font-monospace-body rounded py-0 px-[2px]">
                chkk start
              </code>{' '}
              command will get you started in the Chkk universe.
            </span>
            <span className="w-full whitespace-pre-wrap text-body font-body text-default-font">
              The command will lead you through registering one or more
              Kubernetes Clusters with Chkk and request a Chkk upgrade
              assessment for those registered Kubernetes Clusters.
            </span>
            <span className="w-full whitespace-pre-wrap text-body font-body text-default-font">
              Registering a Kubernetes Cluster with Chkk is the process of
              collecting resources from the Kubernetes Cluster and submitting
              those collected results to Chkk.
            </span>

            <span className="w-full whitespace-pre-wrap text-body font-body text-default-font">
              Type{' '}
              <CopyToClipboard
                className="w-fit inline-flex"
                singleLine={true}
                text={'chkk start'}
                hideIcon={true}
              />{' '}
              in a terminal to begin.
            </span>
            <span className="w-full whitespace-pre-wrap text-body-bold font-body-bold text-default-font">
              {'Sample Run:'}
            </span>
            <CodeBlock className="h-auto w-full flex-none">
              {`
> chkk start
       __    __    __
 .----|  |--|  |--|  |--.
 |  __|     |    <|    <
 |____|__|__|__|__|__|__|

Welcome to Chkk!

This command will help you get started with Chkk.

It will guide you through the process of logging in to your Chkk account and
registering any Kubernetes Clusters if desired.

You may also request an upgrade assessment for any of your registered Kubernetes
Clusters.

────────────────────────────────────────────────────────────────────────────────────────────────────────
Let's get you logged in to your Chkk account.
please confirm the code LCZS-DKDB at https://auth.chkk.io/activate?user_code=LCZS-DKDB
            `}
            </CodeBlock>
          </StepBase>
          <StepBase
            stepTitle="Register a Kubernetes Cluster"
            stepBody=""
            stepNumber="3"
            actionButtons={<></>}
            stepTitleTooltip={<></>}
            badges={<></>}
            defaultOpen={true}
          >
            <span className="w-full whitespace-pre-wrap text-body font-body text-default-font">
              After completing the login in your Browser, in your terminal you
              will see a successful login message and a prompt to register a
              Kubernetes Cluster with Chkk:
            </span>

            <CodeBlock>
              {` successfully logged in as "Jane Doe" (jane.doe@example.com) into "demo-chkk-org" (org_3c2732f7-9328-477d-910b-54cd94ade5c0).
 
Do you want to register any Kubernetes Clusters with Chkk? (Y/n) 
`}
            </CodeBlock>

            <span className="w-full whitespace-pre-wrap text-body font-body text-default-font">
              Hit Enter or &quot;Y&quot; to start the registration process. We
              will look in your kubeconfig for Kubernetes Clusters and ask you
              to select one or more of those Kubernetes Clusters to register
              with Chkk.
            </span>

            <CodeBlock>
              {`Found 1 total Kubernetes Clusters in your kubeconfig.

Select the clusters you would like to register. Resources from the selected
clusters will be collected and submitted to Chkk for analysis.

Use the arrow keys to navigate: ↓ ↑ → ← 
Select "Submit" to submit choices.
    Submit 
  → ✔   kind-charlie (https://127.0.0.1:39775) 
`}
            </CodeBlock>

            <span className="w-full whitespace-pre-wrap text-body font-body text-default-font">
              Use the arrow keys to navigate, hit Enter to select one or more of
              the Kubernetes Clusters and navigate up to the Submit line and hit
              Enter again.
            </span>

            <span className="w-full whitespace-pre-wrap text-body font-body text-default-font">
              We will ask you to confirm your selection:
            </span>

            <CodeBlock>
              {`You made the following 1 selections:

1: kind-charlie (https://127.0.0.1:39775)

Are you sure you want to submit these? Enter 'n' to select again. (Y/n) 
`}
            </CodeBlock>

            <span className="w-full whitespace-pre-wrap text-body font-body text-default-font">
              Hit Enter or &quot;Y&quot; to collect resources from the selected
              Kubernetes Clusters and register them with Chkk.
            </span>

            <CodeBlock>
              {`Registering 1 Kubernetes Cluster(s) with Chkk...

  registering kind-charlie (https://127.0.0.1:39775)...

✅ cluster "kind-charlie" resource collection successful. 49 resource groups collected.

To view information about your cluster, do:

  chkk cluster get k8scl_13673ce4-1cbd-454a-aa31-67b722fadc99
`}
            </CodeBlock>

            <span className="w-full whitespace-pre-wrap text-body font-body text-default-font">
              Your newly-registered Kubernetes Cluster will then be displayed
              along with a prompt asking if you would like to request a Chkk
              Upgrade Assessment for this Kubernetes Cluster:
            </span>

            <CodeBlock>{`The following 1 Kubernetes Clusters are active in your Chkk account:

┌────────────────────────────────────────────┬──────────────┬────────┬─────────────┬──────────┬──────────────┬──────────┐
│                     ID                     │     NAME     │ STATUS │ ENVIRONMENT │ PROVIDER │ KUBE VERSION │ #  NODES │
├────────────────────────────────────────────┼──────────────┼────────┼─────────────┼──────────┼──────────────┼──────────┤
│ k8scl_13673ce4-1cbd-454a-aa31-67b722fadc99 │ kind-charlie │ active │             │ KinD     │ 1.28         │ 2        │
└────────────────────────────────────────────┴──────────────┴────────┴─────────────┴──────────┴──────────────┴──────────┘
showing 1-1 of 1 records.

Would you like to request an upgrade assessment for any of these Kubernetes Clusters? (Y/n)
`}</CodeBlock>
          </StepBase>
          <StepBase
            stepTitle="Request an Upgrade Assessment"
            stepBody=""
            stepNumber="4"
            actionButtons={<></>}
            stepTitleTooltip={<></>}
            badges={<></>}
            defaultOpen={true}
          >
            <span className="w-full whitespace-pre-wrap text-body font-body text-default-font">
              A Chkk Upgrade Assessment is a report that alerts you to potential
              issues with an upgrade of your Kubernetes Cluster and recommends
              upgrade versions for your Cloud Native Projects.
            </span>

            <span className="w-full whitespace-pre-wrap text-body font-body text-default-font">
              Hit Enter or &quot;Y&quot; to select one or more Kubernetes
              Clusters for an Upgrade Assessment.
            </span>

            <CodeBlock>{` Use the arrow keys to navigate: ↓ ↑ → ←
? Select a cluster to request an upgrade assessment for:
▸ kind-charlie (1.28) (KinD)
`}</CodeBlock>

            <span className="w-full whitespace-pre-wrap text-body font-body text-default-font">
              Select a Kubernetes Cluster and hit Enter.
            </span>

            <CodeBlock>{`You selected "kind-charlie (1.28) (KinD)". Is this correct? Enter 'n' to select again. (Y/n)

Requesting upgrade assessment for kind-charlie (1.28) (KinD)...

(optional) enter the name for the upgrade assessment
charlie
✅ request for upgrade assessement for cluster "k8scl_13673ce4-1cbd-454a-aa31-67b722fadc99" successful.
... waiting for upgrade assessment to complete. this will take a few minutes ...
`}</CodeBlock>

            <span className="w-full whitespace-pre-wrap text-body font-body text-default-font">
              The Upgrade Assessment will take a few minutes to generate and
              then you will see:
            </span>

            <CodeBlock>{`completed!

To view your upgrade assessment, do:

chkk upgradeassessment get ua_a59a7fff-f3c2-4443-9f26-fe97cd187def

Learn more about Chkk upgrade assessments at: https://docs.chkk.io/product/upgrade-copilot/assessments
`}</CodeBlock>
          </StepBase>
        </div>
      </div>
    </Page>
  );
}
